/**
 * Fordonskort — fliken «Bokningsinformation» (endast om fordonet ligger i minst en utlämningsplats-pool).
 */

import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useTranslation } from '@/store/languageStore';
import api from '@/services/api/client';
import { bookingApi, type VehicleBookingBlock } from '@/services/booking/bookingApi';
import type { VehiclePickupPlacement } from '@/types/vehicle';
import DateTimeLocalInput from '@/components/common/DateTimeLocalInput';
import {
  datetimeLocalInputToUtcIso,
  formatDateTimeLocal,
  toDatetimeLocalInputValue,
} from '@/utils/dateUtils';

const BOOKING_DESC_MAX = 50;
const BOOKING_EMAIL_NOTE_MAX = 2000;

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <h3 className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-textDefault mb-3">
      <span className="inline-block w-1 self-stretch min-h-[1rem] rounded-sm bg-primary" aria-hidden />
      {children}
    </h3>
  );
}

function fallbackStaffLine(registrationNumber: string, transmission: string | null | undefined): string {
  const reg = registrationNumber.trim();
  const tx = transmission?.trim() ?? '';
  if (reg && tx) return `${reg} — ${tx}`;
  return reg || tx || '—';
}

export interface VehicleBookingInfoTabProps {
  vehicleId: number;
  placements: VehiclePickupPlacement[];
  registrationNumber: string;
  /** Växellåda för reservrad (DB + ev. Car.info i kortet) */
  transmissionDisplay: string | null | undefined;
  initialBookingDescription: string | null | undefined;
  initialBookingConfirmationEmailNote: string | null | undefined;
  /** Vilotid (min) mellan bokningar för fordonet. null = ärv pool/org, 0 = ingen, >0 = överstyr */
  initialBookingRestMinutes: number | null | undefined;
  linkedFlinkeyProductId: string | null;
  linkedFlinkeyBoxType: string | null;
  onSaved: () => void;
}

const VehicleBookingInfoTab: React.FC<VehicleBookingInfoTabProps> = ({
  vehicleId,
  placements,
  registrationNumber,
  transmissionDisplay,
  initialBookingDescription,
  initialBookingConfirmationEmailNote,
  initialBookingRestMinutes,
  linkedFlinkeyProductId,
  linkedFlinkeyBoxType,
  onSaved,
}) => {
  const { t } = useTranslation();
  const [draftDesc, setDraftDesc] = useState(initialBookingDescription ?? '');
  const [draftEmailNote, setDraftEmailNote] = useState(initialBookingConfirmationEmailNote ?? '');
  const [draftRestMinutes, setDraftRestMinutes] = useState(
    initialBookingRestMinutes != null ? String(initialBookingRestMinutes) : ''
  );
  const [savingRest, setSavingRest] = useState(false);
  const [saving, setSaving] = useState(false);
  const [blocks, setBlocks] = useState<VehicleBookingBlock[]>([]);
  const [blockStart, setBlockStart] = useState('');
  const [blockEnd, setBlockEnd] = useState('');
  const [blockWorking, setBlockWorking] = useState(false);

  useEffect(() => {
    setDraftDesc(initialBookingDescription ?? '');
    setDraftEmailNote(initialBookingConfirmationEmailNote ?? '');
    setDraftRestMinutes(initialBookingRestMinutes != null ? String(initialBookingRestMinutes) : '');
  }, [initialBookingDescription, initialBookingConfirmationEmailNote, initialBookingRestMinutes, vehicleId]);

  const loadBlocks = useCallback(async () => {
    try {
      const b = await bookingApi.listVehicleBookingBlocks(vehicleId);
      setBlocks(b);
    } catch {
      setBlocks([]);
    }
  }, [vehicleId]);

  useEffect(() => {
    void loadBlocks();
  }, [loadBlocks]);

  useEffect(() => {
    const a = new Date();
    a.setMinutes(0, 0, 0);
    a.setHours(a.getHours() + 1);
    const b = new Date(a.getTime() + 2 * 60 * 60 * 1000);
    setBlockStart(toDatetimeLocalInputValue(a));
    setBlockEnd(toDatetimeLocalInputValue(b));
  }, [vehicleId]);

  const createBlock = useCallback(async () => {
    setBlockWorking(true);
    try {
      const startAt = datetimeLocalInputToUtcIso(blockStart);
      const endAt = datetimeLocalInputToUtcIso(blockEnd);
      await bookingApi.createVehicleBookingBlock(vehicleId, { startAt, endAt });
      toast.success(t('vehicleCard.booking.blockSaved'));
      await loadBlocks();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      toast.error(msg);
    } finally {
      setBlockWorking(false);
    }
  }, [blockStart, blockEnd, vehicleId, loadBlocks, t]);

  const removeBlock = useCallback(
    async (blockId: string) => {
      if (!window.confirm(t('vehicleCard.booking.blockRemoveConfirm'))) return;
      setBlockWorking(true);
      try {
        await bookingApi.deleteVehicleBookingBlock(vehicleId, blockId);
        toast.success(t('vehicleCard.booking.blockRemoved'));
        await loadBlocks();
      } catch (e: unknown) {
        const msg = e instanceof Error ? e.message : String(e);
        toast.error(msg);
      } finally {
        setBlockWorking(false);
      }
    },
    [vehicleId, loadBlocks, t]
  );

  const saveRestMinutes = useCallback(async () => {
    setSavingRest(true);
    try {
      const trimmed = draftRestMinutes.trim();
      let value: number | null;
      if (trimmed === '') {
        value = null;
      } else {
        const n = Math.floor(Number(trimmed));
        if (!Number.isFinite(n) || n < 0) {
          toast.error(t('vehicleCard.booking.restMinutesInvalid'));
          setSavingRest(false);
          return;
        }
        value = Math.min(n, 1440);
      }
      const res = await api.put<{ success: boolean; error?: string }>(`/api/vehicles/${vehicleId}`, {
        booking_rest_minutes: value,
      });
      if (!res.data.success) {
        throw new Error(res.data.error || 'Save failed');
      }
      toast.success(t('vehicleCard.booking.restMinutesSaved'));
      onSaved();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      toast.error(msg);
    } finally {
      setSavingRest(false);
    }
  }, [draftRestMinutes, vehicleId, onSaved, t]);

  const previewForStaff = draftDesc.trim()
    ? draftDesc.trim()
    : fallbackStaffLine(registrationNumber, transmissionDisplay);

  const saveBookingFields = useCallback(async () => {
    setSaving(true);
    try {
      const descTrim = draftDesc.trim().slice(0, BOOKING_DESC_MAX);
      const noteTrim = draftEmailNote.trim().slice(0, BOOKING_EMAIL_NOTE_MAX);
      const res = await api.put<{ success: boolean; error?: string }>(`/api/vehicles/${vehicleId}`, {
        booking_description: descTrim === '' ? null : descTrim,
        booking_confirmation_email_note: noteTrim === '' ? null : noteTrim,
      });
      if (!res.data.success) {
        throw new Error(res.data.error || 'Save failed');
      }
      toast.success(t('vehicleCard.booking.bookingDescSaved'));
      onSaved();
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : String(e);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  }, [draftDesc, draftEmailNote, vehicleId, onSaved, t]);

  return (
    <div className="space-y-6 text-sm">
      <div className="rounded-md border border-primary/40 bg-primary/5 px-3 py-2 text-textDefault">
        {t('vehicleCard.booking.banner')}
      </div>

      <section>
        <SectionTitle>{t('vehicleCard.booking.sectionDescription')}</SectionTitle>
        <p className="text-textSecondary text-xs mb-2">{t('vehicleCard.booking.descriptionHint')}</p>
        <textarea
          value={draftDesc}
          onChange={(e) => setDraftDesc(e.target.value.slice(0, BOOKING_DESC_MAX))}
          rows={2}
          maxLength={BOOKING_DESC_MAX}
          className="w-full rounded-lg border border-border bg-bgBase px-3 py-2 text-textDefault placeholder:text-textTertiary focus:outline-none focus:ring-2 focus:ring-primary/40 resize-y min-h-[3rem]"
          placeholder={fallbackStaffLine(registrationNumber, transmissionDisplay)}
          aria-label={t('vehicleCard.booking.sectionDescription')}
        />
        <p className="text-textTertiary text-xs mt-1 text-right">
          {t('vehicleCard.booking.charCount', { current: draftDesc.length, max: BOOKING_DESC_MAX })}
        </p>
        <p className="text-textSecondary text-xs mt-2">{t('vehicleCard.booking.previewForStaff')}</p>
        <div className="rounded-lg border border-border bg-bgContainer px-3 py-2 mt-1 text-textDefault whitespace-pre-wrap min-h-[2.5rem]">
          {previewForStaff}
        </div>
      </section>

      <section>
        <SectionTitle>{t('vehicleCard.booking.sectionEmailNote')}</SectionTitle>
        <p className="text-textSecondary text-xs mb-2">{t('vehicleCard.booking.emailNoteHint')}</p>
        <textarea
          value={draftEmailNote}
          onChange={(e) => setDraftEmailNote(e.target.value.slice(0, BOOKING_EMAIL_NOTE_MAX))}
          rows={4}
          maxLength={BOOKING_EMAIL_NOTE_MAX}
          className="w-full rounded-lg border border-border bg-bgBase px-3 py-2 text-textDefault placeholder:text-textTertiary focus:outline-none focus:ring-2 focus:ring-primary/40 resize-y min-h-[5rem]"
          placeholder={t('vehicleCard.booking.emailNotePlaceholder')}
          aria-label={t('vehicleCard.booking.sectionEmailNote')}
        />
        <p className="text-textTertiary text-xs mt-1 text-right">
          {t('vehicleCard.booking.charCountLong', { current: draftEmailNote.length, max: BOOKING_EMAIL_NOTE_MAX })}
        </p>
      </section>

      <div>
        <button
          type="button"
          onClick={() => void saveBookingFields()}
          disabled={saving}
          className="px-4 py-2 rounded-lg bg-primary text-white text-sm font-medium hover:opacity-90 disabled:opacity-50"
        >
          {saving ? t('common.saving') : t('vehicleCard.booking.saveBookingDesc')}
        </button>
      </div>

      <section>
        <SectionTitle>{t('vehicleCard.booking.sectionSlot')}</SectionTitle>
        <p className="text-textSecondary mb-3">{t('vehicleCard.booking.slotHint')}</p>
        <div className="rounded-lg border border-border bg-bgCard p-4">
          <label
            className="block text-xs font-semibold uppercase text-textSecondary mb-1"
            htmlFor={`rest-minutes-${vehicleId}`}
          >
            {t('vehicleCard.booking.restMinutesLabel')}
          </label>
          <p className="text-textSecondary text-xs mb-2">{t('vehicleCard.booking.restMinutesHint')}</p>
          <div className="flex flex-wrap items-center gap-3">
            <input
              id={`rest-minutes-${vehicleId}`}
              type="number"
              min={0}
              max={1440}
              step={5}
              value={draftRestMinutes}
              onChange={(e) => setDraftRestMinutes(e.target.value)}
              placeholder={t('vehicleCard.booking.restMinutesPlaceholder')}
              className="w-32 rounded-lg border border-border bg-bgBase px-3 py-2 text-textDefault text-sm"
            />
            <span className="text-textSecondary text-sm">{t('vehicleCard.booking.restMinutesUnit')}</span>
            <button
              type="button"
              onClick={() => void saveRestMinutes()}
              disabled={savingRest}
              className="px-4 py-2 rounded-lg bg-primary text-white text-sm font-medium hover:opacity-90 disabled:opacity-50"
            >
              {savingRest ? t('common.saving') : t('vehicleCard.booking.restMinutesSave')}
            </button>
          </div>
        </div>
      </section>

      <section>
        <SectionTitle>{t('vehicleCard.booking.sectionPoolLocker')}</SectionTitle>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="rounded-lg border border-border bg-bgCard p-4">
            <h4 className="text-xs font-semibold uppercase text-textSecondary mb-2">
              {t('vehicleCard.booking.poolTitle')}
            </h4>
            <ul className="space-y-2 text-textDefault">
              {placements.map((p) => (
                <li key={p.pickupLocationVehicleId} className="border-b border-border pb-2 last:border-0 last:pb-0">
                  <div className="font-medium">{p.pickupLocationName}</div>
                  {p.displayName ? (
                    <div className="text-textSecondary text-xs">{p.displayName}</div>
                  ) : null}
                  {p.resourceTypeName ? (
                    <div className="text-textTertiary text-xs">{p.resourceTypeName}</div>
                  ) : null}
                  <div className="text-xs mt-1 text-textSecondary">
                    {p.isAvailable ? t('vehicleCard.booking.available') : t('vehicleCard.booking.unavailable')}
                  </div>
                </li>
              ))}
            </ul>
            <Link
              to="/booking/pickup-locations"
              className="mt-3 inline-block text-xs font-medium text-primary hover:underline"
            >
              {t('vehicleCard.booking.goToPickupLocations')} →
            </Link>
            <Link
              to="/booking/vehicle-pools"
              className="mt-2 ml-3 inline-block text-xs font-medium text-primary hover:underline"
            >
              {t('vehicleCard.booking.goToVehiclePools')} →
            </Link>
          </div>
          <div className="rounded-lg border border-border bg-bgCard p-4">
            <h4 className="text-xs font-semibold uppercase text-textSecondary mb-2">
              {t('vehicleCard.booking.lockerTitle')}
            </h4>
            {linkedFlinkeyProductId ? (
              <div className="text-textDefault">
                <div className="font-medium">{linkedFlinkeyProductId}</div>
                {linkedFlinkeyBoxType ? (
                  <div className="text-textSecondary text-xs mt-1">{linkedFlinkeyBoxType}</div>
                ) : null}
              </div>
            ) : (
              <p className="text-textSecondary text-xs">{t('vehicleCard.booking.noLockerLinked')}</p>
            )}
          </div>
        </div>
      </section>

      <section>
        <SectionTitle>{t('vehicleCard.booking.sectionBlock')}</SectionTitle>
        <p className="text-textSecondary mb-3">{t('vehicleCard.booking.blockHint')}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
          <div>
            <label className="block text-xs font-medium text-textSecondary mb-1" htmlFor={`block-start-${vehicleId}`}>
              {t('vehicleCard.booking.blockStart')}
            </label>
            <DateTimeLocalInput id={`block-start-${vehicleId}`}
              value={blockStart}
              onChange={(e) => setBlockStart(e.target.value)}
              className="w-full rounded-lg border border-border bg-bgBase px-3 py-2 text-textDefault text-sm"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-textSecondary mb-1" htmlFor={`block-end-${vehicleId}`}>
              {t('vehicleCard.booking.blockEnd')}
            </label>
            <DateTimeLocalInput id={`block-end-${vehicleId}`}
              value={blockEnd}
              onChange={(e) => setBlockEnd(e.target.value)}
              className="w-full rounded-lg border border-border bg-bgBase px-3 py-2 text-textDefault text-sm"
            />
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <button
            type="button"
            disabled={blockWorking || !blockStart || !blockEnd}
            onClick={() => void createBlock()}
            className="px-4 py-2 rounded-lg bg-primary text-white text-sm font-medium hover:opacity-90 disabled:opacity-50"
          >
            {blockWorking ? t('common.saving') : t('vehicleCard.booking.blockCreate')}
          </button>
          <Link to="/booking/calendar" className="text-sm font-medium text-primary hover:underline">
            {t('vehicleCard.booking.openCalendar')} →
          </Link>
          <Link to="/booking/collision-bookings" className="text-sm font-medium text-primary hover:underline">
            {t('vehicleCard.booking.blockCollisionLink')}
          </Link>
        </div>
        <h4 className="text-xs font-semibold uppercase text-textSecondary mb-2">{t('vehicleCard.booking.blockListTitle')}</h4>
        {blocks.length === 0 ? (
          <p className="text-textTertiary text-xs">{t('vehicleCard.booking.blockNone')}</p>
        ) : (
          <ul className="space-y-2 text-sm">
            {blocks.map((blk) => (
              <li
                key={blk.id}
                className="flex flex-wrap items-center justify-between gap-2 rounded border border-border bg-bgCard px-3 py-2"
              >
                <span className="text-textDefault">
                  {(blk.registrationNumber ?? registrationNumber).trim()} —{' '}
                  {formatDateTimeLocal(blk.startAt)} → {formatDateTimeLocal(blk.endAt)}
                </span>
                <button
                  type="button"
                  disabled={blockWorking}
                  onClick={() => void removeBlock(blk.id)}
                  className="text-xs font-medium text-red-600 hover:underline disabled:opacity-50"
                >
                  {t('vehicleCard.booking.blockRemove')}
                </button>
              </li>
            ))}
          </ul>
        )}
        <p className="text-textTertiary text-xs mt-3">
          {t('vehicleCard.booking.regHint', { reg: registrationNumber })}
        </p>
      </section>
    </div>
  );
};

export default VehicleBookingInfoTab;
